import { useDeviceType } from '@cian/ui-kit';
import * as React from 'react';
import { useDispatch, useSelector } from 'react-redux';

import { useLastMessageName, usePreviewAvatarNode } from './hooks';
import {
  trackSwipeDeleteClick,
  trackSwipeMarkAsReadClick,
  trackSwipeMarkAsStarredClick,
  trackSwipeMoveToArchiveClick,
  trackSwipeRemoveFromArchiveClick,
  trackSwipeUnblockClick,
  trackSwipeUnmarkAsStarredClick,
} from './tracking';
import { markChatsAsRead } from '../../actions/actionsChatsList';
import { openChatActionModal } from '../../actions/chatActionModal';
import { addChatToSelected, openChatFromListItem, removeChatFromSelected } from '../../actions/chatList';
import {
  markChatAsStarred,
  moveToArchive,
  removeFromArchive,
  removeFromSpam,
  unmarkChatAsStarred,
} from '../../actions/chatOperations';
import { openUserCard } from '../../actions/userCard/openUserCard';
import { ChatCompanionTitle } from '../../components/ChatCompanionTitle';
import { ChatListItem, Preview, EChatListItemSwipeButtonType } from '../../components/ChatListItem';
import { useChatListItemCompanion } from '../../hooks/useChatListItemCompanion';
import { hostTypeSelector, chatListSelector, userIdSelector } from '../../selectors';
import { EChatAction } from '../../types/chatActionModal';
import { IChat } from '../../types/chatList';
import { EDeviceType } from '../../types/deviceType';
import { EHostType } from '../../types/hostType';
import { EOutcomingStatus } from '../../types/messages';
import { formatShortDate } from '../../utils/formatShortDate';
import { getChatHeaderTitle } from '../../utils/getChatHeaderTitle';

type TChatListItemContainerProps = {
  chat: IChat;
  openedChatRef?: React.RefObject<HTMLDivElement | null>;
  isActive: boolean;
};

const ChatListItemContainerComponent: React.FC<TChatListItemContainerProps> = ({ chat, openedChatRef, isActive }) => {
  const dispatch = useDispatch();

  const userId = useSelector(userIdSelector);
  const hostType = useSelector(hostTypeSelector);
  const device = useDeviceType();

  const { currentRoute, status: chatListStatus, isChangeListActive, selectedChats } = useSelector(chatListSelector);

  const { lastMessage, offer, users, isStarred, availableActions } = chat;
  const { type, files, userId: lastMessageUserId } = lastMessage || {};

  const companion = useChatListItemCompanion(users);
  const previewAvatarNode = usePreviewAvatarNode({ users });
  const lastMessageName = useLastMessageName({ users, lastMessageUserId: lastMessageUserId || 0 });
  const opponent = users.find(user => user.chatRole === 'opponent');
  const isUserLastMessage = lastMessage?.userId === userId;

  const isDeletedLastMessage = lastMessage?.status === 'deleted';

  const status = isDeletedLastMessage
    ? undefined
    : isUserLastMessage
      ? lastMessage?.status === 'readByRecipient'
        ? EOutcomingStatus.Read
        : EOutcomingStatus.Fulfilled
      : undefined;

  const previewImage = React.useMemo(() => {
    return offer?.photoUrl || companion?.avatarUrl || '';
  }, [offer, companion]);

  const onChatListItemClick = React.useCallback(() => {
    if (isChangeListActive) {
      dispatch(
        selectedChats.includes(chat.chatId) ? removeChatFromSelected(chat.chatId) : addChatToSelected(chat.chatId),
      );
    } else {
      const isDesktopFrame = hostType === EHostType.Frame;
      const isMobile = device !== EDeviceType.Desktop;

      dispatch(openChatFromListItem({ chatId: chat.chatId, isMobile }));

      if (!isDesktopFrame && !isMobile && companion.companionId) {
        dispatch(openUserCard(companion.companionId));
      }
    }
  }, [chat, companion.companionId, dispatch, isChangeListActive, hostType, device, selectedChats]);

  const onSwipeButtonClick = React.useCallback(
    (type: EChatListItemSwipeButtonType) => {
      switch (type) {
        case EChatListItemSwipeButtonType.Starred: {
          dispatch(markChatAsStarred({ chatId: chat.chatId }));
          trackSwipeMarkAsStarredClick(offer, opponent?.userId);

          return;
        }
        case EChatListItemSwipeButtonType.Unstarred: {
          dispatch(unmarkChatAsStarred({ chatId: chat.chatId }));
          trackSwipeUnmarkAsStarredClick(offer, opponent?.userId);

          return;
        }
        case EChatListItemSwipeButtonType.Archive: {
          dispatch(moveToArchive(chat.chatId));
          trackSwipeMoveToArchiveClick(offer, opponent?.userId);

          return;
        }
        case EChatListItemSwipeButtonType.Delete: {
          dispatch(addChatToSelected(chat.chatId));
          dispatch(
            openChatActionModal({
              buttonText: 'Удалить',
              text: 'Точно хотите удалить чат?',
              chatAction: EChatAction.Delete,
            }),
          );
          trackSwipeDeleteClick(offer, opponent?.userId);

          return;
        }
        case EChatListItemSwipeButtonType.Read: {
          dispatch(markChatsAsRead([chat.chatId]));
          trackSwipeMarkAsReadClick(offer, opponent?.userId);

          return;
        }
        case EChatListItemSwipeButtonType.Return: {
          dispatch(removeFromArchive(chat.chatId));
          trackSwipeRemoveFromArchiveClick(offer, opponent?.userId);

          return;
        }
        case EChatListItemSwipeButtonType.Unblock: {
          dispatch(removeFromSpam(chat.chatId));
          trackSwipeUnblockClick(offer, opponent?.userId);

          return;
        }
        case EChatListItemSwipeButtonType.Unread: {
          return;
        }
      }
    },
    [dispatch, chat.chatId, offer, opponent?.userId],
  );

  const fileLastMessage = React.useMemo(() => {
    const isFileLastMessage = type === 'textAndFiles';

    if (isFileLastMessage && files) {
      const type = files.fileTypes[0];
      const name = files.images?.[0]?.name || files?.documents?.[0]?.name || files?.imagesAsDocuments?.[0]?.name || '';

      return { type, name };
    }

    return undefined;
  }, [files, type]);

  const date = React.useMemo(() => formatShortDate(chat.updated), [chat.updated]);

  const customHeaderTitle = React.useMemo(() => getChatHeaderTitle(chat.header), [chat.header]);

  return (
    <ChatListItem
      openedChatRef={openedChatRef}
      chatId={chat.chatId}
      checkboxChecked={selectedChats.includes(chat.chatId)}
      checkboxShown={isChangeListActive}
      title={<ChatCompanionTitle {...customHeaderTitle} dataTestId="chat_list_item_title" />}
      isActive={isActive}
      fileLastMessage={fileLastMessage}
      isOfferLastMessage={!!lastMessage?.offers?.length}
      isDeletedLastMessage={isDeletedLastMessage}
      message={lastMessage?.text || ''}
      isMultilineMessage={!offer}
      onClick={onChatListItemClick}
      status={status}
      unreadCount={chat.unreadMessagesCount}
      date={date}
      onSwipeButtonClick={onSwipeButtonClick}
      chatInfo={chat.description}
      currentRoute={currentRoute}
      lastMessageName={lastMessageName}
      previewNode={
        <Preview
          isOffer={!!offer?.formattedFullInfo}
          previewAvatarNode={previewAvatarNode}
          previewImage={previewImage}
        />
      }
      isStarred={Boolean(isStarred)}
      availableActions={availableActions}
      chatListStatus={chatListStatus}
    />
  );
};

export const ChatListItemContainer = React.memo(ChatListItemContainerComponent);
